import { normalizeDecimalDraft } from './decimalInput'

const SKIP_INPUT_TYPES = new Set([
  'file',
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'color',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
  'hidden',
  'image',
  'range',
])

/** Arabic-Indic and Persian digits, plus the Arabic decimal mark, become English. */
export function toEnglishDigits(value: string): string {
  if (!/[٠-٩۰-۹٫]/.test(value)) return value
  let out = ''
  for (const ch of value) {
    const code = ch.charCodeAt(0)
    if (code >= 0x0660 && code <= 0x0669) out += String(code - 0x0660)
    else if (code >= 0x06f0 && code <= 0x06f9) out += String(code - 0x06f0)
    else if (code === 0x066b) out += '.'
    else out += ch
  }
  return out
}

function isDecimalField(el: HTMLInputElement): boolean {
  return el.type === 'number' || el.inputMode === 'decimal' || el.inputMode === 'numeric'
}

function rewriteValue(raw: string, caret: number, decimalField: boolean): { value: string; caret: number } {
  if (decimalField) {
    const value = normalizeDecimalDraft(raw)
    return { value, caret: normalizeDecimalDraft(raw.slice(0, caret)).length }
  }
  return { value: toEnglishDigits(raw), caret }
}

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
  setter?.call(el, value)
}

function forceEnglish(el: HTMLInputElement | HTMLTextAreaElement) {
  const decimalField = el instanceof HTMLInputElement && isDecimalField(el)
  const caret = el.selectionStart ?? el.value.length
  const next = rewriteValue(el.value, caret, decimalField)
  if (next.value === el.value) return

  const tracker = (el as HTMLInputElement & { _valueTracker?: { getValue: () => string; setValue: (v: string) => void } })._valueTracker
  const previous = tracker?.getValue?.()
  setNativeValue(el, next.value)
  if (tracker && previous !== undefined) tracker.setValue(previous)
  try {
    el.setSelectionRange(next.caret, next.caret)
  } catch {
    /* number/email inputs can reject a selection */
  }
}

/** Every typed number is stored as English digits, in every field. */
export function installEnglishDigitGuard() {
  document.addEventListener(
    'focusin',
    (event) => {
      const el = event.target
      if (!(el instanceof HTMLInputElement) || !isDecimalField(el)) return
      el.lang = 'en'
      el.dir = 'ltr'
    },
    true,
  )

  document.addEventListener(
    'input',
    (event) => {
      const el = event.target
      if (el instanceof HTMLInputElement) {
        if (SKIP_INPUT_TYPES.has(el.type)) return
        forceEnglish(el)
        return
      }
      if (el instanceof HTMLTextAreaElement) forceEnglish(el)
    },
    true,
  )
}
